/**
 * Implementation of component to create a SSL monitor
 *
 * @author - Harsh Tomar
 */

// Dependencies
import { SyntheticEvent, useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { BiLoaderAlt } from "react-icons/bi";
import { FiPlus } from "react-icons/fi";
import { When } from "react-if";
import { useMutation, useQuery } from "react-query";
import { createMonitor, useGetCurrentUser } from "../../api/reactQuery";
import { getMonitors } from "../../api/requests";
import { httpsUrlValidator, removeScheme } from "../../utils";
import Modal from "../Modal";

export default function CreateSSLMonitor() {
  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [url, setUrl] = useState("");
  const { data: currentUser } = useGetCurrentUser(true);

  const { refetch } = useQuery("monitors", () => getMonitors(), {
    enabled: false,
  });

  useEffect(() => {}, [url]);

  const { mutate } = useMutation(
    (newMutation: { type: string; name: string; url: string }) =>
      createMonitor(newMutation),
    {
      onError: (error: string) => {
        // TODO show plan related from here
        setLoading(false);
        setError(error)
      },
      onSuccess: (response, variable) => {
        toast.success(`Monitoring ${url} for SSL changes`)
        refetch();
        setModalVisible(false);
        setLoading(false);
        setUrl('');
      },
    }
  );

  const _handleSubmit = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      if (!url) {
        setError("Please enter a valid domain");
        return;
      }

      if (!httpsUrlValidator(url)) {
        setError("Invalid URL format. Please check and try again.");
        return;
      }

      mutate({
        type: "SSL_MONITORING",
        url: removeScheme(url),
        name: "Unamed",
      });
      setLoading(true);
    },
    [url]
  );

  return (
    <div>
      <button
        className="btn-primary block"
        onClick={() => setModalVisible(true)}
      >
        <FiPlus className="inline mr-2 -mt-1" />
        New Monitor
      </button>

      <Modal visible={modalVisible} onClose={() => setModalVisible(false)}>
        <div>
          <h2 className="text-2xl text-center mb-4">Monitor new domain</h2>
          <p className="text-gray-600">Enter your domain</p>
          <form onSubmit={_handleSubmit}>
            <input
              className="input-field-primary w-full mt-1 mb-2"
              placeholder="yourdomain.com"
              required
              autoFocus
              onChange={(e) => {
                setUrl(e.target.value);
                setError("");
              }}
              value={url}
            />
            <When condition={!!error}>
              <div>
                <h3 className="text-red-500 text-base font-bold">{error}</h3>
              </div>
            </When>

            <p className="text-gray-600 mt-2">E-mail alerts will be sent to</p>
            <div className="flex flex-wrap">
              {currentUser?.alertTo
                .filter((alert) => alert.type === "EMAIL")
                .map((alert, i) => (
                  <span key={i} className="bg-gray-200 text-gray-500 font-semibold px-2 m-1 rounded-full">
                    {alert.to}
                  </span>
                ))}
            </div>
            <div className="mt-4">
              {loading ? (
                <div className="flex justify-center items-center">
                  <BiLoaderAlt className="animate-spin" size={28} />
                </div>
              ) : (
                <button type="submit" className="btn-primary w-full">
                  Start Monitoring
                </button>
              )}
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
}
